import React, { useState, useEffect, useRef } from 'react';
import { Container, Form, Button, Image, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { apiUrl } from '../apiUrl';

const INDIAN_STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Lakshadweep',
  'Puducherry',
];

const Volunteer: React.FC = () => {
  const [formData, setFormData] = useState({
    volunteerId: '',
    fullName: '',
    mobile: '',
    email: '',
    aadhar: '',
    gender: '',
    dateOfBirth: '',
    state: '',
    city: '',
    pinCode: '',
    address: '',
    skills: [] as string[],
    otherSkill: '',
    availability: '',
    volunteeringArea: '',
    previousExperience: 'No',
    experienceDetails: '',
    notRobot: false,
    photo: null as File | null,
  });
  const [result, setResult] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const [previewPhoto, setPreviewPhoto] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false); // Modal visibility state
  const [showStateOptions, setShowStateOptions] = useState(false);
  const [stateSearchValue, setStateSearchValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const pending = useRef(false);
  const mounted = useRef(true);
  const [validationAttempted, setValidationAttempted] = useState(false);

  useEffect(() => {
    const generateVolunteerId = () => {
      const randomId = 'VOL' + Math.floor(1000 + Math.random() * 9000);
      setFormData((prev) => ({ ...prev, volunteerId: randomId }));
    };
    generateVolunteerId();
  }, []);

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const showResult = (kind: 'success' | 'error', message: string) => { setResult({ kind, message }); setShowModal(true); };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSkillChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setFormData((prev) => {
      const selected = checked ? [...prev.skills, value] : prev.skills.filter((skill) => skill !== value);
      const skills = ['Technical', 'Teaching', 'IT', 'Others'].filter((skill) => selected.includes(skill));
      return { ...prev, skills, otherSkill: skills.includes('Others') ? prev.otherSkill : '' };
    });
  };

  const closeModal = () => setShowModal(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData((prev) => ({ ...prev, photo: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewPhoto(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, notRobot: e.target.checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pending.current || uncertain) return;

    if (!formData.notRobot) {
      showResult('error', 'Please accept the Terms and Conditions.');
      return;
    }
    const hasValidState = INDIAN_STATES.some(
      (option) => option.toLowerCase() === formData.state.trim().toLowerCase()
    );
    if (!hasValidState) {
      showResult('error', 'Please select a valid state from the dropdown list.');
      return;
    }
    if (formData.skills.length === 0) {
      showResult('error', 'Please select at least one skill.');
      return;
    }
    const hasOthersSkill = formData.skills.some((skill) => skill.toLowerCase() === 'others');
    const trimmedOtherSkill = formData.otherSkill.trim();
    if (hasOthersSkill && !trimmedOtherSkill) {
      showResult('error', 'Please specify your other skill.');
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('volunteerId', formData.volunteerId);
    formDataToSend.append('fullName', formData.fullName);
    formDataToSend.append('mobile', formData.mobile);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('aadhar', formData.aadhar || '');
    formDataToSend.append('gender', formData.gender);
    formDataToSend.append('dateOfBirth', formData.dateOfBirth);
    formDataToSend.append('state', formData.state);
    formDataToSend.append('city', formData.city);
    formDataToSend.append('pinCode', formData.pinCode);
    formDataToSend.append('address', formData.address);
    formDataToSend.append('skills', JSON.stringify(formData.skills));
    formDataToSend.append('otherSkill', hasOthersSkill ? trimmedOtherSkill : '');
    formDataToSend.append('availability', formData.availability);
    formDataToSend.append('volunteeringArea', formData.volunteeringArea);
    formDataToSend.append('previousExperience', formData.previousExperience);
    formDataToSend.append('experienceDetails', formData.experienceDetails || '');
    if (formData.photo) {
      formDataToSend.append('photo', formData.photo);
    }

    pending.current = true;
    try {
      setIsSubmitting(true);
      const res = await fetch(apiUrl('/api/volunteers'), {
        method: 'POST',
        body: formDataToSend,
      });

      const data = await res.json().catch(() => null);
      if (!mounted.current) return;
      if (res.ok) {
        if (!data || typeof data.emailQueued !== 'boolean') throw new Error('Uncertain response');
        setValidationAttempted(false);
        showResult('success', data.emailQueued
          ? 'Thank you for registering as a volunteer!'
          : 'Application submitted. Confirmation email could not be queued; please contact the Foundation if needed.');
        const randomId = 'VOL' + Math.floor(1000 + Math.random() * 9000);
        setFormData({
          volunteerId: randomId,
          fullName: '',
          mobile: '',
          email: '',
          aadhar: '',
          gender: '',
          dateOfBirth: '',
          state: '',
          city: '',
          pinCode: '',
          address: '',
          skills: [],
          otherSkill: '',
          availability: '',
          volunteeringArea: '',
          previousExperience: 'No',
          experienceDetails: '',
          notRobot: false,
          photo: null,
        });
        setStateSearchValue('');
        setShowStateOptions(false);
        setPreviewPhoto(null);
      } else {
        if (res.status >= 500) { setUncertain(true); showResult('error', 'We could not confirm whether your application was saved. Please contact the Foundation before retrying to avoid a duplicate.'); }
        else showResult('error', data?.message || 'Error submitting form.');
      }
    } catch (err) {
      if (mounted.current) { setUncertain(true); showResult('error', 'We could not confirm whether your application was saved. Please contact the Foundation before retrying to avoid a duplicate.'); }
    } finally {
      pending.current = false;
      if (mounted.current) setIsSubmitting(false);
    }
  };

  return (
    <div className="sf-involvement-page sf-volunteer-page">
      <section className="sf-involvement-intro tw-py-[clamp(3rem,6vw,5rem)]">
        <Container className="sf-involvement-container">
          <p className="sf-involvement-eyebrow">GET INVOLVED</p>
          <h1>Join Us as a Volunteer</h1>
          <p className="sf-involvement-lead">Support our current focus on free education and learning for children studying up to Class 8.</p>
        </Container>
      </section>

      {/* Volunteer Form Section */}
      <section className="tw-py-[clamp(2.8rem,5vw,4.4rem)]">
        <Container className="sf-involvement-container">
          {/*  <h2 className="text-center mb-5 volunteer-title">Become a Volunteer</h2>*/}
          <div className="sf-involvement-card">
            <h2>Volunteer Registration</h2>
            <p>Fields marked * are required. Mobile numbers must have 10 digits and PIN codes 6 digits. Optional Aadhaar numbers must have 12 digits.</p>
            <p>Current volunteering relates to children's education. Other programme areas listed below represent future interests, not active programmes.</p>
            {previewPhoto && (
              <div className="photo-preview mb-3 text-center">
                <Image src={previewPhoto} alt="Preview" rounded fluid style={{ maxWidth: '200px' }} />
              </div>
            )}
            <Form onSubmit={handleSubmit} onInvalidCapture={() => setValidationAttempted(true)} className={validationAttempted ? 'sf-validation-attempted' : undefined}>
              {validationAttempted && <p className="sf-validation-summary" role="alert">Please correct the highlighted fields. Your browser will explain the first error.</p>}
              <Form.Group className="mb-3" controlId="formVolunteerId">
                <Form.Label className="d-none">Volunteer ID</Form.Label>
                <Form.Control
                  type="text"
                  name="volunteerId"
                  value={formData.volunteerId}
                  readOnly
                  className="volunteer-id-field d-none"
                />
              </Form.Group>
              {/* Row 1: Photo and Full Name */}
              <div className="row mb-3">
               
                <div className="col-md-12">
                  <Form.Group controlId="formFullName">
                    <Form.Label>Full Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      required
                    />
                  </Form.Group>
                </div>
              </div>
              {/* Row 2: Mobile and Email */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group controlId="formMobile">
                    <Form.Label>Mobile</Form.Label>
                    <Form.Control
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      placeholder="Enter your mobile number"
                      pattern="[0-9]{10}"
                      required
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group controlId="formEmail">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      required
                    />
                  </Form.Group>
                </div>
              </div>
              {/* Row 3: Aadhar and Gender */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group controlId="formAadhar">
                    <Form.Label>Aadhar (Optional)</Form.Label>
                    <Form.Control
                      type="text"
                      name="aadhar"
                      value={formData.aadhar}
                      onChange={handleChange}
                      placeholder="Enter your Aadhar number"
                      pattern="[0-9]{12}"
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group controlId="formGender">
                    <Form.Label>Gender</Form.Label>
                    <Form.Control as="select" name="gender" value={formData.gender} onChange={handleChange} required>
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </Form.Control>
                  </Form.Group>
                </div>
              </div>
              {/* Row 4: Date of Birth and State */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group controlId="formDateOfBirth">
                    <Form.Label>Date of Birth</Form.Label>
                    <Form.Control
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group controlId="formState">
                    <Form.Label>State</Form.Label>
                    <div style={{ position: 'relative' }}>
                      <Form.Control
                        type="text"
                        value={stateSearchValue}
                        onFocus={() => setShowStateOptions(true)}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setStateSearchValue(e.target.value);
                          setShowStateOptions(true);
                        }}
                        onBlur={() => {
                          const matched = INDIAN_STATES.find(
                            (option) => option.toLowerCase() === stateSearchValue.trim().toLowerCase()
                          );
                          if (matched) {
                            setStateSearchValue(matched);
                            setFormData((prev) => ({ ...prev, state: matched }));
                          } else {
                            setStateSearchValue(formData.state || '');
                          }
                          setTimeout(() => setShowStateOptions(false), 120);
                        }}
                        placeholder="Search and select your state"
                        required
                      />
                      {showStateOptions && (
                        <div
                          className="list-group"
                          style={{
                            position: 'absolute',
                            zIndex: 20,
                            width: '100%',
                            maxHeight: '180px',
                            overflowY: 'auto',
                            border: '1px solid #dfe5ef',
                            borderRadius: '8px',
                            marginTop: '6px',
                            background: '#fff',
                          }}
                        >
                          {INDIAN_STATES.filter((option) =>
                            option.toLowerCase().includes(stateSearchValue.trim().toLowerCase())
                          )
                            .slice(0, 50)
                            .map((option) => (
                              <button
                                key={option}
                                type="button"
                                className="list-group-item list-group-item-action"
                                onMouseDown={(evt) => evt.preventDefault()}
                                onClick={() => {
                                  setStateSearchValue(option);
                                  setFormData((prev) => ({ ...prev, state: option }));
                                  setShowStateOptions(false);
                                }}
                              >
                                {option}
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                  </Form.Group>
                </div>
              </div>
              {/* Row 5: City and Pin Code */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group controlId="formCity">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Enter your city"
                      required
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group controlId="formPinCode">
                    <Form.Label>Pin Code</Form.Label>
                    <Form.Control
                      type="text"
                      name="pinCode"
                      value={formData.pinCode}
                      onChange={handleChange}
                      placeholder="Enter your pin code"
                      pattern="[0-9]{6}"
                      required
                    />
                  </Form.Group>
                </div>
              </div>
              {/* Row 6: Address (Full Width) */}
              <div className="row mb-3">
                <div className="col-md-12">
                  <Form.Group controlId="formAddress">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Enter your address"
                      required
                    />
                  </Form.Group>
                </div>
              </div>
              {/* Row 7: Skills (Full Width) */}
              <div className="row mb-3">
                <div className="col-md-12">
                  <Form.Group controlId="formSkills">
                    <fieldset aria-describedby="volunteer-skills-help">
                      <legend className="form-label">Skills &amp; Expertise *</legend>
                      <Form.Text id="volunteer-skills-help" className="d-block mb-2">Select at least one skill.</Form.Text>
                      {['Technical', 'Teaching', 'IT', 'Others'].map((skill, index) => (
                        <div className="form-check" key={skill}>
                          <input className="form-check-input" id={`volunteer-skill-${skill}`} type="checkbox" value={skill} checked={formData.skills.includes(skill)} onChange={handleSkillChange} ref={index === 0 ? (input) => { input?.setCustomValidity(formData.skills.length ? '' : 'Please select at least one skill.'); } : undefined} />
                          <label className="form-check-label" htmlFor={`volunteer-skill-${skill}`}>{skill}</label>
                        </div>
                      ))}
                    {formData.skills.includes('Others') && (
                      <Form.Control
                        type="text"
                        name="otherSkill"
                        aria-label="Specify other skill"
                        required
                        value={formData.otherSkill}
                        onChange={handleChange}
                        placeholder="Specify other skill"
                        className="mt-2"
                      />
                    )}
                    </fieldset>
                  </Form.Group>
                </div>
              </div>
              {/* Row 8: Availability and Volunteering Area */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group controlId="formAvailability">
                    <Form.Label>Availability</Form.Label>
                    <Form.Control
                      as="select"
                      name="availability"
                      value={formData.availability}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Availability</option>
                      <option value="DAY">Day</option>
                      <option value="Hours">Hours</option>
                      <option value="Weekend">Weekend</option>
                    </Form.Control>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group controlId="formVolunteeringArea">
                    <Form.Label>Preferred Volunteering Area</Form.Label>
                    <Form.Control
                      as="select"
                      name="volunteeringArea"
                      value={formData.volunteeringArea}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Area</option>
                      <option value="Education">Education</option>
                      <option value="Women Empowerment">Women Empowerment</option>
                      <option value="Environment">Environment</option>
                      <option value="Health">Health</option>
                      <option value="Livelihood">Livelihood</option>
                      <option value="Fundraising">Fundraising</option>
                      <option value="Administrator">Administrator</option>
                    </Form.Control>
                  </Form.Group>
                </div>
              </div>
              {/* Row 9: Previous Experience (Full Width) */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Group controlId="formPreviousExperience">
                    <Form.Label>Previous Volunteer Experience</Form.Label>
                    <Form.Control
                      as="select"
                      name="previousExperience"
                      value={formData.previousExperience}
                      onChange={handleChange}
                      required
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </Form.Control>
                    {formData.previousExperience === 'Yes' && (
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="experienceDetails"
                        aria-label="Previous volunteer experience details"
                        value={formData.experienceDetails}
                        onChange={handleChange}
                        placeholder="Provide details"
                        className="mt-2"
                      />
                    )}
                  </Form.Group>
                </div>
                 <div className="col-md-6">
                  <Form.Group controlId="formPhoto">
                    <Form.Label>Upload Photo (Optional)</Form.Label>
                    <Form.Control type="file" name="photo" accept="image/*" onChange={handleFileChange} />
                    <Form.Text>Optional image file.</Form.Text>
                  </Form.Group>
                </div>
              </div>
              {/* Row 10: Not Robot (Full Width) */}
              <div className="row mb-3">
                <div className="col-md-12">
                  <Form.Group controlId="formNotRobot">
                    <Form.Check
                      type="checkbox"
                      label="I accept the Terms and Conditions *"
                      name="notRobot"
                      checked={formData.notRobot}
                      onChange={handleCheckboxChange}
                      required
                    />
                  </Form.Group>
                </div>
              </div>
              <div className="text-center mt-4">
                {uncertain && <p role="status">The submission result is uncertain. Please contact the Foundation before trying again.</p>}
                <Button
                  variant="primary"
                  type="submit"
                  className="sf-involvement-submit"
                  disabled={isSubmitting || uncertain}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </Form>
          </div>
        </Container>
      </section>

      {/* Success/Error Modal */}
      <Modal show={showModal} onHide={closeModal} centered>
        <Modal.Body className="text-center">
          {result?.kind === 'success' ? (
            <div className="d-flex flex-column align-items-center">
              <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success mb-3" />
              <h4 className="text-success mb-2">Success!</h4>
              <p>{result.message}</p>
            </div>
          ) : (
            <div className="d-flex flex-column align-items-center">
              <FontAwesomeIcon icon={faTimesCircle} size="3x" className="text-danger mb-3" />
              <h4 className="text-danger mb-2">Error!</h4>
              <p>{result?.message}</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={closeModal}>Close</Button></Modal.Footer>
      </Modal>
    </div>
  );
};

export default Volunteer;
